/**
 * GPS Permission Hook
 * 
 * Handles GPS permission requests and provides user-friendly feedback
 */

import { useState, useEffect, useCallback } from 'react';

export type GPSPermissionStatus = 'prompt' | 'granted' | 'denied' | 'unavailable';

interface UseGPSPermissionReturn {
  permissionStatus: GPSPermissionStatus;
  requestPermission: () => Promise<boolean>;
  isSupported: boolean;
  error: string | null;
}

export function useGPSPermission(): UseGPSPermissionReturn {
  const [permissionStatus, setPermissionStatus] = useState<GPSPermissionStatus>('prompt');
  const [error, setError] = useState<string | null>(null);
  const [isSupported, setIsSupported] = useState(false);

  useEffect(() => {
    // Check if geolocation is supported
    if (!navigator.geolocation) {
      setIsSupported(false);
      setPermissionStatus('unavailable');
      setError('GPS is niet beschikbaar op dit apparaat');
      return;
    }

    setIsSupported(true);

    // Check if Permissions API is available (Chrome, Edge, Firefox)
    if ('permissions' in navigator) {
      navigator.permissions
        .query({ name: 'geolocation' as PermissionName })
        .then((result) => {
          setPermissionStatus(result.state as GPSPermissionStatus);
          
          result.onchange = () => {
            setPermissionStatus(result.state as GPSPermissionStatus);
          };
        })
        .catch(() => {
          // Permissions API not fully supported, fallback to prompt
          setPermissionStatus('prompt');
        });
    } else {
      // Permissions API not available, assume prompt
      setPermissionStatus('prompt');
    }
  }, []);

  const requestPermission = useCallback(async (): Promise<boolean> => {
    if (!navigator.geolocation) {
      setError('GPS is niet beschikbaar op dit apparaat');
      return false;
    }

    return new Promise((resolve) => {
      navigator.geolocation.getCurrentPosition(
        () => {
          setPermissionStatus('granted');
          setError(null);
          resolve(true);
        },
        (err) => {
          switch (err.code) {
            case err.PERMISSION_DENIED:
              setPermissionStatus('denied');
              setError('GPS toegang is geweigerd. Activeer GPS in je browser instellingen.');
              break;
            case err.POSITION_UNAVAILABLE:
              setError('Locatie informatie is niet beschikbaar.');
              break;
            case err.TIMEOUT:
              setError('Het ophalen van je locatie duurde te lang.');
              break;
            default:
              setError('Er is een onbekende fout opgetreden bij het ophalen van je locatie.');
          }
          resolve(false);
        },
        {
          enableHighAccuracy: true,
          timeout: 5000,
          maximumAge: 0,
        }
      );
    });
  }, []);

  return {
    permissionStatus,
    requestPermission,
    isSupported,
    error,
  };
}
